import { useState, useEffect } from 'react';
import { authHeaders } from '../../utils/auth';
import { apiFetch } from '../../utils/api';
import type { Group, GroupMember, User, ApiErrorBody } from '../../types';

type Props = {
  group: Group;
  onClose: () => void;
  onMemberCountChange: (groupId: number, delta: number) => void;
};

export default function GroupMembersModal({ group, onClose, onMemberCountChange }: Props) {
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [allArtists, setAllArtists] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [removing, setRemoving] = useState<number | null>(null);
  const [adding, setAdding] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      apiFetch(`/api/groups/${group.id}/members`, { headers: authHeaders() })
        .then(r => r.json() as Promise<GroupMember[]>),
      apiFetch('/api/users?role=artist', { headers: authHeaders() })
        .then(r => r.json() as Promise<User[]>),
    ])
      .then(([m, a]) => {
        setMembers(m ?? []);
        setAllArtists(a ?? []);
      })
      .catch(() => setError('Failed to load group data'))
      .finally(() => setLoading(false));
  }, [group.id]);

  const memberUserIds = new Set(members.map(m => m.userId));

  const nonMembers = allArtists.filter(u => !memberUserIds.has(u.id));

  const filteredNonMembers = search.trim()
    ? nonMembers.filter(u => {
        const q = search.toLowerCase();
        return (
          u.fName.toLowerCase().includes(q) ||
          u.lName.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q)
        );
      })
    : nonMembers;

  const handleRemove = async (userId: number) => {
    setRemoving(userId);
    setError(null);
    try {
      const res = await apiFetch(`/api/groups/${group.id}/members/${userId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const data: ApiErrorBody = await res.json();
        setError(data.message ?? 'Failed to remove member');
        return;
      }
      setMembers(prev => prev.filter(m => m.userId !== userId));
      onMemberCountChange(group.id, -1);
    } catch {
      setError('Failed to remove member');
    } finally {
      setRemoving(null);
    }
  };

  const handleAdd = async (user: User) => {
    setAdding(user.id);
    setError(null);
    try {
      const res = await apiFetch(`/api/groups/${group.id}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ userId: user.id }),
      });
      const data: ApiErrorBody & { data?: GroupMember } = await res.json();
      if (!res.ok) {
        setError(data.message ?? 'Failed to add member');
        return;
      }
      setMembers(prev =>
        [...prev, data.data!].sort((a, b) => a.lName.localeCompare(b.lName) || a.fName.localeCompare(b.fName))
      );
      onMemberCountChange(group.id, 1);
    } catch {
      setError('Failed to add member');
    } finally {
      setAdding(null);
    }
  };

  return (
    <div className='modal-backdrop' onClick={onClose}>
      <div className='modal group-members-modal' onClick={e => e.stopPropagation()}>
        <div className='modal-header'>
          <h2 className='modal-title'>{group.name}</h2>
          <button className='modal-close' onClick={onClose}>&#x2715;</button>
        </div>

        {loading && <p className='group-modal-loading'>Loading...</p>}

        {!loading && (
          <div className='group-modal-body'>
            <div className='group-modal-section'>
              <h3 className='group-modal-section-title'>Members ({members.length})</h3>
              {members.length === 0
                ? <p className='group-modal-empty'>No members yet.</p>
                : (
                  <ul className='group-member-list'>
                    {members.map(m => (
                      <li key={m.userId} className='group-member-row'>
                        <span className='group-member-name'>{m.fName} {m.lName}</span>
                        <button
                          className='group-member-remove-btn'
                          onClick={() => handleRemove(m.userId)}
                          disabled={removing === m.userId}
                        >
                          {removing === m.userId ? 'Removing...' : 'Remove'}
                        </button>
                      </li>
                    ))}
                  </ul>
                )
              }
            </div>

            <div className='group-modal-section'>
              <h3 className='group-modal-section-title'>Add Artists</h3>
              <input
                className='group-search-input'
                type='text'
                placeholder='Filter by name or email...'
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <ul className='group-artist-checklist'>
                {filteredNonMembers.length === 0 ? (
                  <li className='group-search-empty'>
                    {search.trim() ? 'No matching artists' : 'All artists are already members'}
                  </li>
                ) : (
                  filteredNonMembers.map(u => (
                    <li key={u.id} className='group-artist-check-row'>
                      <span className='group-artist-check-name'>{u.fName} {u.lName}</span>
                      {Array.isArray(u.groups) && u.groups.length > 0 && (
                        <span className='user-group-chips' style={{ flex: 1, marginLeft: '8px' }}>
                          {u.groups.slice(0, 2).map(g => (
                            <span key={g} className='user-group-chip'>{g}</span>
                          ))}
                          {u.groups.length > 2 && (
                            <span className='user-group-chip user-group-chip--more'>+{u.groups.length - 2}</span>
                          )}
                        </span>
                      )}
                      <button
                        className='group-member-remove-btn'
                        onClick={() => handleAdd(u)}
                        disabled={adding === u.id}
                      >
                        {adding === u.id ? 'Adding...' : 'Add'}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>

            {error && <p className='group-modal-error'>{error}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
